import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, Icon } from '@sakhtar/design-system/react';
import { MobilePageHead } from '../../shell/MobilePageHead.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { MASTER_DATA_ENTITIES } from '../../lib/masterDataConfig.js';
import type { ShellContextResponse } from '../../lib/types/shell.js';
import { QuickTaskForm } from '../projects/QuickTaskForm.js';
import { canSeeNavEntry, canSeeTab } from '../../shell/navAccess.js';
import { useAccessContext } from '../../shell/useNavAccess.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Mobile App.dc.html §FRAME 06 ADD ACTION SHEET
 * canonical pattern: permission-aware create-action list
 * state reference: available actions
 *
 * Lists exactly the create actions that exist today (5 master-data
 * entities + invite user) — not the Design Board's full catalogue (sales
 * order, accounting document, etc.), since those creation flows aren't
 * built yet. Grows as more of the route inventory's ADAPTER_REQUIRED /
 * RUNTIME_READY create actions get client pages.
 *
 * «کار جدید» (Projects entitlement only) opens `QuickTaskForm` inline on
 * this page — the same project-yes/no → me-or-colleague → deadline flow as
 * the desktop topbar's quick add. `?new=task` opens it directly.
 */
export function AddPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const access = useAccessContext();
  const context = useApiGet<ShellContextResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/admin/shell-context` : null,
    [slug],
  );
  if (!slug) return null;
  const base = tenantBasePath(slug);
  const projectsEntitled =
    canSeeNavEntry('projects', access) &&
    context.status === 'ready' &&
    context.data.phase === 'ready' &&
    (context.data.entitlements?.entitledModuleSlugs.includes('projects') ?? false);
  const taskFormOpen = projectsEntitled && searchParams.get('new') === 'task';

  if (taskFormOpen) {
    return (
      <div style={{ padding: 16 }}>
        <MobilePageHead title="کار جدید" />
        <Card>
          <QuickTaskForm slug={slug} onCancel={() => setSearchParams({})} />
        </Card>
      </div>
    );
  }

  const actions = [
    { id: 'invite', label: 'دعوت کاربر', icon: 'users' as const, href: `${base}/admin/users` },
    ...MASTER_DATA_ENTITIES.map((entity) => ({
      id: entity.kind,
      label: `${entity.label} جدید`,
      icon: entity.icon,
      href: `${base}/master-data/${entity.pathSegment}/new`,
    })),
  ].filter((action) =>
    action.id === 'invite'
      ? canSeeNavEntry('users-access', access)
      : canSeeTab('master-data', action.id, access),
  );

  return (
    <div style={{ padding: 24 }}>
      <MobilePageHead title="افزودن" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {projectsEntitled ? (
          <button
            type="button"
            onClick={() => setSearchParams({ new: 'task' })}
            style={{
              textAlign: 'start',
              background: 'none',
              border: 0,
              padding: 0,
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 44 }}>
                <Icon name="tasks" />
                <span>کار جدید</span>
              </div>
            </Card>
          </button>
        ) : null}
        {actions.map((action) => (
          <Link
            key={action.id}
            to={action.href}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Icon name={action.icon} />
                <span>{action.label}</span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
